/**
 * missing-page.rules.ts
 *
 * Pure logic for the "What's still missing?" step (`ItemsMissingStepComponent`),
 * which every PO routes through after check-off. Missing is dependent — it's
 * derived from the PO vs. its logs/receipts vs. what was just checked off —
 * so the app computes a per-line guess the user corrects, rather than asking
 * them to declare "missing" while marking arrivals. On a fresh or ledgered PO
 * the guess is trustworthy math; on a legacy PO (pre-feature logs the ledger
 * can't see) it may overstate and the user reconciles by hand. These helpers
 * decide which lines the page shows, classify parts vs. qty lines, and compute
 * the outstanding amounts — kept out of the component so they stay
 * unit-testable and shared with the items-string builder.
 */

import { CheckableLineItem, DamageDisposition, LineDamage, lineKey } from '../../../service/receiving-mobile/log-entry-draft.store';
import { MissingItem, MissingReason } from '../../../models/logs/MissingItem';
import { isRaggedLine, splitRaggedParts } from './line-receipts.rules';

/** Per-line damage lookup keyed by `lineKey`; defaults to empty for damage-agnostic callers. */
type DamageByLine = Record<string, LineDamage>;

/**
 * The PO lines the missing page reconciles: still-outstanding lines only — the
 * Partials and the untouched. Complete lines (fully arrived this load) drop off,
 * as do canceled, already-closed, and off-PO rows (none of which the page acts on).
 *
 * A `damageByLine` may be passed so a line marked Complete/Partial but whose
 * damaged goods were refused/returned is treated as outstanding (it did not
 * really arrive). Omitting it keeps the plain arrival-based behavior — the
 * missing-page UI passes nothing (refused/returned items are decided already
 * and don't need reconciling); the items-string and payload builders pass it.
 *
 * @param rows - The Items step working rows from the draft store.
 * @param damageByLine - per-line damage reports (optional).
 * @returns The subset of rows still needing a "missing vs. done" decision.
 */
export function outstandingLines(rows: CheckableLineItem[], damageByLine: DamageByLine = {}): CheckableLineItem[] {
  return rows.filter((r) => isOutstandingLine(r, damageByLine));
}

/** Whether a single row belongs on the missing page / missing set (see `outstandingLines`). */
export function isOutstandingLine(row: CheckableLineItem, damageByLine: DamageByLine = {}): boolean {
  if (row.isOffPo || row.isCanceled || row.isClosed) { return false; }
  // Refused/returned goods didn't stay — the line is outstanding even if it was
  // tapped Complete/Partial before the disposition was chosen.
  if (isRefusedOrReturned(row, damageByLine)) { return true; }
  return row.status !== 'complete';
}

/** The damage disposition chosen for a line, or null when it isn't flagged damaged. */
export function dispositionFor(row: CheckableLineItem, damageByLine: DamageByLine): DamageDisposition | null {
  return damageByLine[lineKey(row)]?.disposition ?? null;
}

/** True when a line's damaged goods were refused or returned (i.e. not kept). */
export function isRefusedOrReturned(row: CheckableLineItem, damageByLine: DamageByLine): boolean {
  const d = dispositionFor(row, damageByLine);
  return d === 'Refuse' || d === 'Return';
}

/**
 * How many units physically arrived on this line THIS delivery, before any
 * refusal — the countable amount for a Complete / Partial / off-PO line. Returns
 * `null` when there's no count to work with: a ragged (parts-listed) line, or an
 * untouched line. Mirrors the receipt builder's arrival quantities so the two agree.
 */
export function arrivedQty(row: CheckableLineItem): number | null {
  if (row.isOffPo) {
    const q = row.quantityReceived ?? (row.quantity ? Number(row.quantity) : null);
    return q != null && !isNaN(q) ? q : null;
  }
  // Ragged lines arrive as parts, not a count — no per-unit quantity to damage.
  if (isRaggedLine(row.receipts) || splitRaggedParts(row.partsReceived).length > 0) { return null; }
  if (row.status === 'complete') {
    if (row.quantityExpected != null && row.quantityReceived != null) {
      return Math.max(row.quantityExpected - row.quantityReceived, 0);
    }
    const q = Number(row.quantity);
    return isNaN(q) ? null : q;
  }
  if (row.status === 'partial') {
    const q = Number(row.receivedQty);
    return isNaN(q) ? null : q;
  }
  return null;
}

/**
 * How many of a damaged line's units are damaged, capped at what actually
 * arrived. 0 when the line isn't flagged damaged, or when it's ragged/uncountable
 * (there the disposition applies to the whole line, not a quantity).
 */
export function damagedQty(row: CheckableLineItem, damageByLine: DamageByLine): number {
  const damage = damageByLine[lineKey(row)];
  if (!damage) { return 0; }
  const arrived = arrivedQty(row);
  if (arrived == null) { return 0; }
  const q = Math.max(Math.floor(Number(damage.quantity)) || 0, 0);
  return Math.min(q, arrived);
}

/** Damaged units that were NOT kept (refuse/return) — these become missing. 0 for kept/none/ragged. */
export function refusedReturnedQty(row: CheckableLineItem, damageByLine: DamageByLine): number {
  return isRefusedOrReturned(row, damageByLine) ? damagedQty(row, damageByLine) : 0;
}

/**
 * Net units received on this line THIS delivery = arrived − refused/returned
 * damaged units. `null` when the line has no count (ragged). This is what counts
 * toward the line and drives the outstanding math.
 */
export function netReceivedQty(row: CheckableLineItem, damageByLine: DamageByLine): number | null {
  const arrived = arrivedQty(row);
  if (arrived == null) { return null; }
  return Math.max(arrived - refusedReturnedQty(row, damageByLine), 0);
}

/** Why a line is outstanding: its disposition (Refused/Returned) when refused/returned, else a plain Shortfall. */
export function missingReasonFor(row: CheckableLineItem, damageByLine: DamageByLine): MissingReason {
  const d = dispositionFor(row, damageByLine);
  if (d === 'Refuse') { return 'Refused'; }
  if (d === 'Return') { return 'Returned'; }
  return 'Shortfall';
}

/**
 * The outstanding items on this delivery — plain shortfalls plus refused/returned
 * goods — as structured `MissingItem`s. Single source feeding both the expediting
 * email payload and the free-text items string's missing/refused/returned clauses.
 *
 * A line contributes when it's outstanding (see `isOutstandingLine`) and hasn't
 * been human-closed (`completesLine`). Because damage is a quantity, ONE line can
 * yield TWO entries: the refused/returned damaged units (their own reason) AND the
 * plain shortfall left over (ordered − arrived). Parts/ragged lines carry
 * `quantityMissing: null` (no countable amount).
 *
 * When `poComplete` is set there is by definition nothing missing (everything was
 * received, this delivery or earlier), so the set is empty — same rule the items
 * string applies to its MISSING clause, so the two can't disagree. This matters
 * most on a legacy PO: the skip path prefills outstanding amounts from ledger math
 * that can't see the earlier untracked deliveries, so untouched lines carry an
 * overstated guess that must not become a missing record or an expediting email.
 *
 * @param rows - The Items step working rows.
 * @param damageByLine - per-line damage reports (drives the refused/returned split).
 * @param poComplete - Whether "This completes the PO" was checked; short-circuits to [].
 * @returns The outstanding items, in row order (a line may contribute up to two).
 */
export function buildMissingItems(
  rows: CheckableLineItem[],
  damageByLine: DamageByLine = {},
  poComplete: boolean = false,
): MissingItem[] {
  if (poComplete) { return []; }
  const items: MissingItem[] = [];
  for (const row of rows) {
    if (!isOutstandingLine(row, damageByLine) || row.completesLine) { continue; }

    const ragged = arrivedQty(row) == null;
    const refusedReturned = refusedReturnedQty(row, damageByLine);

    // 1) The refused/returned damaged units (their own reason). On a ragged line
    //    there's no count, so it's the whole line (qty null).
    if (isRefusedOrReturned(row, damageByLine)) {
      const reason = dispositionFor(row, damageByLine) === 'Refuse' ? 'Refused' : 'Returned';
      items.push(makeMissingItem(row, ragged ? null : refusedReturned, reason));
    }

    // 2) The plain shortfall left over (total still owed minus the refused units).
    if (ragged || isPartsLine(row)) {
      // No count: a non-refused ragged/parts line is a plain shortfall (qty null).
      if (!isRefusedOrReturned(row, damageByLine)) {
        items.push(makeMissingItem(row, null, 'Shortfall'));
      }
    } else {
      const total = outstandingAmount(row, damageByLine) ?? 0;
      const shortfall = Math.max(total - refusedReturned, 0);
      if (shortfall > 0) {
        items.push(makeMissingItem(row, shortfall, 'Shortfall'));
      }
    }
  }
  return items;
}

/** Assembles one `MissingItem` from a line row. */
function makeMissingItem(row: CheckableLineItem, quantityMissing: number | null, reason: MissingReason): MissingItem {
  return {
    poLineSequence: row.poLineSequence ?? null,
    itemNumber: row.itemNumber,
    itemDescription: row.description,
    quantityMissing,
    reason,
  };
}

/**
 * A ragged Partial line where the user listed specific parts rather than a
 * quantity (§4: `quantityReceived: null`, parts text in notes). The missing page
 * asks such a line "still missing the rest, or is the line done?" instead of
 * showing an editable outstanding qty — and "still missing" prompts for text
 * describing what's missing (`missingNotes`).
 *
 * @param row - An outstanding row.
 * @returns True when the row carries parts text.
 */
export function isPartsLine(row: CheckableLineItem): boolean {
  return row.status === 'partial' && !!(row.partsReceived ?? '').trim();
}

/**
 * The line's full expected quantity, from the ledger rollup when present or
 * the raw ordered qty otherwise (mock/degrade rows carry no rollup).
 *
 * @param row - Any line row.
 * @returns The expected quantity, or `null` when the row carries no usable qty.
 */
export function expectedQty(row: CheckableLineItem): number | null {
  if (row.quantityExpected != null) { return row.quantityExpected; }
  const total = Number(String(row.totalQty ?? '').trim()) || Number(String(row.quantity ?? '').trim());
  return total > 0 ? total : null;
}

/**
 * The computed still-missing amount on a qty line:
 * `expected − quantityReceived(ledger) − receivedThisLoad`, floored at 0.
 * `receivedThisLoad` is the page-one Partial qty (0 for an untouched line).
 * On a fresh/ledgered PO this is the trustworthy missing guess; on a legacy PO
 * it may overstate (legacy receipts aren't in the ledger) — either way it's
 * editable on the page.
 *
 * When the line's damaged goods were refused/returned, this-delivery arrival is
 * excluded (the goods weren't kept), so the whole ordered-minus-ledger amount is
 * outstanding.
 *
 * @param row - An outstanding qty line.
 * @param damageByLine - per-line damage reports (optional; drives the refuse/return exclusion).
 * @returns The non-negative outstanding quantity, or `null` when the row
 *   carries no usable expected qty to compute from.
 */
export function outstandingQtyPrefill(row: CheckableLineItem, damageByLine: DamageByLine = {}): number | null {
  const expected = expectedQty(row);
  if (expected == null) { return null; }
  const ledgerReceived = row.quantityReceived ?? 0;
  // Net of any refused/returned damaged units — those didn't stay, so they're
  // still outstanding. `netReceivedQty` is null for ragged/untouched lines, where
  // a partial's typed qty (or 0) is the right "received this load".
  const net = netReceivedQty(row, damageByLine);
  const receivedThisLoad = net != null ? net : (row.status === 'partial' ? (Number(row.receivedQty) || 0) : 0);
  return Math.max(expected - ledgerReceived - receivedThisLoad, 0);
}

/**
 * The line's effective still-missing amount for display/notes: the user's
 * edited value when the missing page has one, else the computed prefill.
 *
 * @param row - An outstanding qty line.
 * @param damageByLine - per-line damage reports (optional; forwarded to the prefill).
 * @returns The non-negative outstanding quantity, or `null` when unknown.
 */
export function outstandingAmount(row: CheckableLineItem, damageByLine: DamageByLine = {}): number | null {
  const edited = String(row.outstandingQty ?? '').trim();
  if (edited !== '') { return Math.max(Number(edited) || 0, 0); }
  return outstandingQtyPrefill(row, damageByLine);
}

/**
 * Whether an edited outstanding value closes the line: editing it to zero
 * (nothing left missing) is a human close (§3). A blank value is treated as
 * "still unknown / missing", not a close.
 *
 * @param outstandingValue - The current value of the outstanding-qty input.
 * @returns True when the line should be closed (`completesLine: true`).
 */
export function closesLine(outstandingValue: string | null | undefined): boolean {
  const trimmed = (outstandingValue ?? '').trim();
  if (trimmed === '') { return false; }
  return (Number(trimmed) || 0) <= 0;
}

/**
 * Applies the missing page's per-line prefill to a set of rows WITHOUT the page
 * being open. This lets a non-legacy PO — whose received-vs-ordered math is
 * trustworthy — skip the reconcile page entirely while its missing amounts are
 * still auto-filled (the user can still review/adjust them from the Review step).
 *
 * Mirrors `ItemsMissingStepComponent._prefillOutstanding`, and shares its
 * "only touch rows not yet initialized" guard (`missingReviewed == null`), so
 * it's idempotent and never clobbers a user's edits when run again (e.g. when
 * the same rows are later opened in the Review modal).
 *
 * @param rows - The Items step working rows from the draft store.
 * @param damageByLine - per-line damage reports.
 * @param assumeAllMissing - when true (legacy), pre-select EVERY outstanding
 *   line as still missing (`missingReviewed: true`, never auto-done) — the
 *   ledger can't be trusted, so the user deselects the lines an earlier
 *   untracked delivery already covered. When false (fresh/ledgered), trust the
 *   guess: a line the ledger shows fully received (prefill ≤ 0) is pre-marked
 *   done. The qty guess is pre-filled either way, and every line is reviewed.
 * @returns A rows array with outstanding lines prefilled; touched rows are new
 *   objects, untouched rows keep their identity (so callers can detect changes
 *   by reference).
 */
export function autoFillMissingRows(
  rows: CheckableLineItem[],
  damageByLine: DamageByLine,
  assumeAllMissing: boolean,
): CheckableLineItem[] {
  return rows.map((row) => {
    if (!isOutstandingLine(row, damageByLine) || row.missingReviewed != null) { return row; }

    const parts = isPartsLine(row);
    const prefill = parts ? null : outstandingQtyPrefill(row, damageByLine);
    const outstandingQty = (!parts && prefill != null && (row.outstandingQty == null || row.outstandingQty === ''))
      ? String(prefill) : row.outstandingQty;

    // Legacy assumes all-missing (done=false, the user deselects what arrived);
    // non-legacy trusts the guess, so a 0 remainder means the line is already done.
    const done = !assumeAllMissing && !parts && prefill != null && prefill <= 0;
    return { ...row, outstandingQty, missingReviewed: true, completesLine: done };
  });
}
